import { ApplePay, Client } from 'braintree-web';
import { OnSuccessPromise, logApplePayError, startBraintreeSession } from 'utils/applePay';
import {
  UseProcessorApplePayParams,
  UseProcessorApplePayResults,
} from 'hooks/apple-pay/useApplePay';
import { useActions } from '@tock/shared/utils/connect';
import { useCallback, useEffect, useState } from 'react';
import { useMountEffect } from '@tock/hooks';

export const useApplePayForBraintree = (
  params: UseProcessorApplePayParams
): UseProcessorApplePayResults => {
  const [braintreeClient, setBraintreeClient] = useState<Client>();
  const [applePayInstance, setApplePayInstance] = useState<ApplePay>();

  const { actions } = useActions();

  const canMakePaymentRequests =
    params.isSupportedTockEnvironment &&
    !!window.ApplePaySession &&
    !!window.braintree &&
    !!window.braintree.applePay;

  /**
   * Initilize the Braintree client if the browser/device/Tock environment
   * supports Apple Pay
   */
  useMountEffect(() => {
    if (!canMakePaymentRequests) {
      return;
    }

    if (
      window.ApplePaySession.supportsVersion(3) === false ||
      window.ApplePaySession.canMakePayments() === false
    ) {
      params.setIsSupportedDevice(false);
      return;
    }

    params.setIsSupportedDevice(true);

    actions.paymentCard
      .getBraintreeClientInstance()
      .then((client: unknown) => {
        setBraintreeClient(client as Client);
      })
      .catch(() => {
        logApplePayError('BRAINTREE', 'init', 'Failed to create Braintree client');
        params.setFailed(true);
      });
  });

  /**
   * Once the Braintree client is initialized, check if the device
   * has a valid credit card in their Apple Wallet.
   */
  useEffect(() => {
    if (!canMakePaymentRequests) {
      return;
    }

    const merchantId = braintreeClient?.getConfiguration()?.gatewayConfiguration?.merchantId;

    if (merchantId) {
      window.ApplePaySession.canMakePaymentsWithActiveCard(merchantId)
        .then(params.setHasCardInAppleWallet)
        .catch(() => {
          logApplePayError(
            'BRAINTREE',
            'init',
            'Failed to check if device can make payments with active card'
          );
          params.setFailed(true);
        });
    }
  }, [braintreeClient, canMakePaymentRequests, params]);

  /**
   * Once the Braintree client is initialized and the Apple Pay is supported,
   * initialize the Apple Pay instance.
   */
  useEffect(() => {
    if (!canMakePaymentRequests) {
      return;
    }

    if (!applePayInstance && braintreeClient) {
      window.braintree.applePay
        .create({ client: braintreeClient })
        .then(setApplePayInstance)
        .catch(() => {
          logApplePayError('BRAINTREE', 'init', 'Failed to create Braintree Apple Pay instance');
          params.setFailed(true);
        });
    }
  }, [canMakePaymentRequests, applePayInstance, braintreeClient, params]);

  const startSession = useCallback(
    (onSuccess: OnSuccessPromise) => {
      if (!braintreeClient || !applePayInstance || !params.amountCents) {
        return Promise.reject('Apple Pay failed to load');
      }

      return startBraintreeSession(
        applePayInstance,
        braintreeClient.getConfiguration().gatewayConfiguration.applePayWeb,
        params.amountCents,
        onSuccess
      );
    },
    [applePayInstance, braintreeClient, params.amountCents]
  );

  return {
    isEnabled: canMakePaymentRequests,
    isLoading: canMakePaymentRequests && (!braintreeClient || !applePayInstance),
    startSession: startSession,
  };
};
